import React from 'react'

import { GetCard } from '@web/walter-lib/api'
import { Alert, Button, FormattedHTMLMessage } from '@web/walter-ui-kit'
import { useIntl } from 'react-intl'
import s from './s.module.css'
import { lang } from './lang'
import { globalLang } from '~globalLang'
import { useCardBalance } from '~api'

interface Props {
    card?: GetCard
    onTopUp: () => void
}

const getType = (status: GetCard['status'], balance?: string) => {
    if (status === 'PENDING') {
        return 'warning'
    }

    if (status === 'SUSPENDED' || status === 'BLOCKED' || status === 'FAILED') {
        return 'error'
    }

    if (Number(balance) === 0) {
        return 'accent'
    }

    if (Number(balance) < 0) {
        return 'error'
    }

    return 'error'
}

const getContent = ({
    status,
    balance,
    onTopUp,
    supportLink,
    isBalanceLoading,
}: {
    status?: GetCard['status']
    balance?: string
    isBalanceLoading?: boolean
    supportLink: string
    onTopUp: () => void
}) => {
    if (status === 'SUSPENDED') {
        return null
    }

    if (status === 'PENDING') {
        return (
            <div className={s.alertContent}>
                <FormattedHTMLMessage {...lang.pending} />
                <Button dataPointer="contact" href={supportLink} role="anchor" fullWidth>
                    <FormattedHTMLMessage {...lang.contact} />
                </Button>
            </div>
        )
    }

    if (status === 'BLOCKED') {
        return (
            <div className={s.alertContent}>
                <FormattedHTMLMessage {...lang.blocked} />
                <Button dataPointer="contact" href={supportLink} role="anchor" fullWidth>
                    <FormattedHTMLMessage {...lang.contact} />
                </Button>
            </div>
        )
    }

    if (status === 'FAILED') {
        return (
            <div className={s.alertContent}>
                <FormattedHTMLMessage {...lang.failed} />
                <Button dataPointer="contact" href={supportLink} role="anchor" fullWidth>
                    <FormattedHTMLMessage {...lang.contact} />
                </Button>
            </div>
        )
    }

    if (Number(balance) === 0 && !isBalanceLoading) {
        return (
            <div className={s.alertContent}>
                <FormattedHTMLMessage {...lang.zeroBalance} />
                <Button dataPointer="topup_zero_state" mode="primary" onClick={onTopUp} fullWidth>
                    <FormattedHTMLMessage {...lang.topUp} />
                </Button>
            </div>
        )
    }

    if (Number(balance) < 0 && !isBalanceLoading) {
        return (
            <div className={s.alertContent}>
                <FormattedHTMLMessage {...lang.underZeroBalance} values={{ supportLink }} />
                <Button dataPointer="topup_under_zero_balance" onClick={onTopUp} fullWidth>
                    <FormattedHTMLMessage {...lang.topUp} />
                </Button>
            </div>
        )
    }

    return null
}

function Status({ card, onTopUp }: Props) {
    const intl = useIntl()
    const supportLink = intl.formatMessage(globalLang.contactSupportLink)

    const { data: balance, isLoading } = useCardBalance({ id: card?.id || '' })

    if (!card) {
        return null
    }

    const content = getContent({
        status: card?.status,
        onTopUp,
        balance: balance?.available_balance,
        isBalanceLoading: isLoading,
        supportLink,
    })

    if (!content) {
        return null
    }

    return (
        <div className={s.alert}>
            <Alert
                className={s.alertWrap}
                icon={null}
                dataPointer="card_alert"
                text={content}
                type={getType(card?.status, balance?.available_balance)}
            />
        </div>
    )
}

export default Status
